import type {Auth, User} from 'firebase/auth'
import type {PropsWithChildren} from 'react'
import {createContext, useCallback, useContext, useEffect, useReducer} from 'react'
import {useFirebaseAuth} from '@goschool/react-firebase'

import type {GoSchoolInvitation, GoSchoolUser, GoSchoolUserRole, Organization} from '@goschool/model'
import {isGoSchoolUserRole, useGoSchoolFunctions, useGoSchoolUser} from '@goschool/model'
import type {DocumentReference, DocumentSnapshot} from 'firebase/firestore'
import {getDoc, updateDoc} from 'firebase/firestore'
import * as Sentry from '@sentry/react'

interface AuthState {
  user: User | undefined | null;
  userSnapshot: DocumentSnapshot<GoSchoolUser> | undefined | null
  roles: GoSchoolUserRole[] | undefined | null;
  state: 'pending' | 'success' | 'error';
}


export interface UserContextType {
  user: User | undefined | null;
  goSchoolUser: GoSchoolUser | undefined | null
  roles: GoSchoolUserRole[];
  state: 'pending' | 'success' | 'error';
  auth: Auth;
  acceptInvitation: (invitationRef: DocumentReference<GoSchoolInvitation>) => Promise<void>
  selectOrganization: (ref: DocumentReference<Organization>) => Promise<void>
}

export const UserContext =
  createContext<UserContextType | undefined>(undefined)

export function UserContextProvider({children}: PropsWithChildren) {
  const {auth, user} = useFirebaseAuth()
  const {acceptInvitation: invokeAcceptInvitation} = useGoSchoolFunctions()

  const [authState, dispatchAuth] = useReducer(userStateReducer, {
    user: undefined, userSnapshot: undefined, roles: undefined,
    state: 'pending'
  })


  const updateRoles = useCallback(
    async (user: User | null, forceRefresh = false) => {
      const roles = await getRoles(user, forceRefresh)
      dispatchAuth({type: 'updateRoles', roles})
    }, []
  )


  useEffect(() => {
    if (user !== undefined) {
      if (user === null) {
        dispatchAuth({type: 'updateUser', user: null})
        dispatchAuth({type: 'updateRoles', roles: []})
        dispatchAuth({type: 'updateSnapshot', snapshot: null})
      } else {
        dispatchAuth({type: 'updateUser', user})
        updateRoles(user, true)
      }
    } else {
      dispatchAuth({type: 'updateUser', user: undefined})
      dispatchAuth({type: 'updateRoles', roles: undefined})
      dispatchAuth({type: 'updateSnapshot', snapshot: undefined})
    }
  }, [updateRoles, user])

  const userSnapshot = useGoSchoolUser(authState.user)

  const selectOrganization = useCallback(
    async (ref: DocumentReference<Organization>) => {
      if (userSnapshot == null) {
        return
      }
      await updateDoc(userSnapshot.ref, {
        organization: ref
      })

    }, [userSnapshot]
  )


  const acceptInvitation = useCallback(
    async (invitationRef: DocumentReference<GoSchoolInvitation>) => {
      await invokeAcceptInvitation(invitationRef)
      const invitation = (await getDoc(invitationRef))?.data()
      const user = auth.currentUser
      if (user != null) {
        updateRoles(user, true)
      }
      if (invitation?.organization != null) {
        await selectOrganization(invitation.organization)
      }

    }, [auth, invokeAcceptInvitation, selectOrganization, updateRoles]
  )


  useEffect(() => {
    dispatchAuth({type: 'updateSnapshot', snapshot: userSnapshot})
  }, [userSnapshot])

  //useWhatChanged([authState, user, userSnapshot], 'authState, user, userSnapshot')

  return <UserContext.Provider value={{
    user: authState.user,
    goSchoolUser: authState.userSnapshot?.data() ?? null,
    roles: authState.roles ?? [],
    state: authState.state,
    auth, selectOrganization, acceptInvitation
  }}>{children}</UserContext.Provider>
}

export function useUserContext() {
  const context = useContext(UserContext)
  if (context == null) {
    throw new Error('useAuthContext must be used within an AuthContextProvider')
  }
  return context
}


async function getRoles(user: User | null, forceRefresh = false): Promise<GoSchoolUserRole[]> {
  if (user == null) {
    return []
  }
  const tokenResult = await user.getIdTokenResult(forceRefresh)
  if ('roles' in tokenResult.claims && Array.isArray(tokenResult.claims.roles)) {
    return tokenResult.claims.roles.filter(isGoSchoolUserRole)
  }
  return []
}

type UserStateAction =
  { type: 'updateUser', user: User | null | undefined } |
  { type: 'updateRoles', roles: GoSchoolUserRole[] | null | undefined } |
  { type: 'updateSnapshot', snapshot: DocumentSnapshot<GoSchoolUser> | null | undefined }

function userStateReducer(currentState: AuthState, action: UserStateAction): AuthState {
  let updated = false
  const state = {...currentState}
  switch (action.type) {
  case 'updateUser': {
    if (!(currentState.user === action.user || currentState.user != null && currentState.user?.uid === action.user?.uid)) {
      state.user = action.user
      if (currentState.userSnapshot == null || currentState.userSnapshot.ref.id !== action.user?.uid) {
        state.userSnapshot = undefined
      }
      state.roles = undefined
      updated = true
    }
    break
  }
  case 'updateRoles': {
    if (currentState.roles !== action.roles) {
      state.roles = action.roles ?? []
      updated = true
    }
    break
  }
  case 'updateSnapshot': {
    if (currentState.user === undefined) {
      state.userSnapshot = undefined
      updated = true
    } else if (currentState.user == null) {
      state.userSnapshot = null
      state.roles = []
      updated = true
    } else if (currentState.user.uid === action.snapshot?.ref.id) {
      state.userSnapshot = action.snapshot
      updated = true
    }
  }
    break
  }

  if (!updated) {
    return currentState
  }

  if (state.user === undefined) {
    if (state.userSnapshot === undefined && state.roles === undefined) {
      state.state = 'pending'
      state.roles = undefined
    } else {
      // This can happen transiently during auth/snapshot updates; record a breadcrumb
      // instead of creating a Sentry issue to avoid noise in production.
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'User is undefined but snapshot or roles are defined',
        level: 'info',
        data: {state, action}
      })
      state.state = 'error'
    }
  } else if (state.user == null) {
    if (state.roles === undefined || state.userSnapshot === undefined) {
      state.state = 'pending'
    } else if (state.userSnapshot == null && state.roles?.length === 0) {
      state.state = 'success'
    } else {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'User is null but snapshot or roles are defined',
        level: 'info',
        data: {state, action}
      })
      state.state = 'error'
    }
  } else {
    if (state.userSnapshot === undefined || state.roles === undefined) {
      state.state = 'pending'
    } else if (state.userSnapshot == null || !state.userSnapshot.exists() || state.userSnapshot.ref.id !== state.user?.uid) {
      // This frequently occurs briefly during auth changes; avoid creating Sentry issues
      // for expected race conditions. Keep a breadcrumb for diagnostics instead.
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'User is defined but snapshot is null or does not match',
        level: 'info',
        data: {state, action}
      })
      state.state = 'error'
    } else {
      state.state = 'success'
    }
  }
  
  return state
}
